import { ZERO_WEI } from '@kwenta/sdk/constants'
import { formatCryptoCurrency, formatDollars, isZero } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'

import { type FC, memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import CalculatorIcon from 'assets/svg/futures/calculator-icon.svg'
import DepositWithdrawArrowsIcon from 'assets/svg/futures/deposit-withdraw-arrows.svg'
import InputHeaderRow from 'components/Input/InputHeaderRow'
import BaseInputTitle, { InputTitleSpan as BaseInputTitleSpan } from 'components/Input/InputTitle'
import NumericInput from 'components/Input/NumericInput'
import { FlexDiv } from 'components/layout/flex'
import { setOpenModalWithParams } from 'state/app/reducer'
import { editTradeSizeInput } from 'state/futures/actions'
import {
	selectLeverageSide,
	selectMarketIndexPrice,
	selectTradePanelInputs,
} from 'state/futures/common/selectors'
import { setProfitCalculatorVisible } from 'state/futures/reducer'
import {
	selectAvailableOi,
	selectIsIsolatedMarginProvider,
	selectMaxUsdSizeInput,
	selectPosition,
	selectSelectedInputDenomination,
	selectTradePrice,
	selectTradeSizeInputsDisabled,
} from 'state/futures/selectors'
import { ManageModalType } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { unserializePositionDetails } from 'utils/futures'

import TradePanelAvailableMargin from '../Trade/TradePanelAvailableMargin'

import { EVENT_NAME } from 'constants/matomoEvents'
import { useAnalytics } from 'hooks/useAnalytics'
import { DrawerTab } from 'sections/accounts/constants'
import { DenominationToggle } from './DenominationToggle'

type OrderSizingProps = {
	isMobile?: boolean
}

const OrderSizing: FC<OrderSizingProps> = memo(({ isMobile }) => {
	const dispatch = useAppDispatch()
	const { susdSize, nativeSize } = useAppSelector(selectTradePanelInputs)
	const position = useAppSelector(selectPosition)
	const marketAssetRate = useAppSelector(selectMarketIndexPrice)
	const orderPrice = useAppSelector(selectTradePrice)
	const assetInputType = useAppSelector(selectSelectedInputDenomination)
	const maxUsdInputAmount = useAppSelector(selectMaxUsdSizeInput)
	const tradeSide = useAppSelector(selectLeverageSide)
	const availableOi = useAppSelector(selectAvailableOi)
	const sizeInputsDisabled = useAppSelector(selectTradeSizeInputsDisabled)
	const isIsolated = useAppSelector(selectIsIsolatedMarginProvider)
	const { trackDepositFlow } = useAnalytics()

	const isDisabled = useMemo(() => {
		return sizeInputsDisabled
	}, [sizeInputsDisabled])

	const tradePrice = useMemo(
		() => (orderPrice ? wei(orderPrice) : wei(marketAssetRate)),
		[orderPrice, marketAssetRate]
	)

	const positionDetails = useMemo(() => {
		if (!position) return null
		return unserializePositionDetails(position.details)
	}, [position])

	const increasingPosition = !positionDetails?.side || positionDetails?.side === tradeSide

	const availableOiUsd = useMemo(() => {
		return increasingPosition
			? wei(availableOi[tradeSide].usd)
			: wei(availableOi[tradeSide].usd).add(positionDetails?.margin.notionalValue || 0)
	}, [increasingPosition, availableOi, tradeSide, positionDetails?.margin.notionalValue])

	const availableOiNative = useMemo(() => {
		return increasingPosition
			? wei(availableOi[tradeSide].native)
			: wei(availableOi[tradeSide].native).add(positionDetails?.size || 0)
	}, [tradeSide, availableOi, increasingPosition, positionDetails?.size])

	const maxNativeValue = useMemo(() => {
		const max = !isZero(tradePrice) ? wei(maxUsdInputAmount).div(tradePrice) : ZERO_WEI
		return max.lt(availableOiNative) ? max : availableOiNative
	}, [tradePrice, maxUsdInputAmount, availableOiNative])

	const onChangeValue = useCallback(
		(_: any, v: string) => {
			dispatch(editTradeSizeInput(v, assetInputType))
		},
		[dispatch, assetInputType]
	)

	const invalid = useMemo(() => {
		return (
			(assetInputType === 'usd' && susdSize !== '' && wei(maxUsdInputAmount).lte(susdSize || 0)) ||
			(assetInputType === 'native' && nativeSize !== '' && maxNativeValue.lte(nativeSize || 0)) ||
			availableOiUsd.lt(susdSize || 0)
		)
	}, [assetInputType, maxNativeValue, nativeSize, availableOiUsd, maxUsdInputAmount, susdSize])

	const handleShowProfitCalculator = useCallback(() => {
		if (!isDisabled) {
			dispatch(setProfitCalculatorVisible(true))
		}
	}, [dispatch, isDisabled])

	const handleOnManageClick = useCallback(() => {
		if (!isDisabled) {
			trackDepositFlow(EVENT_NAME.MANAGE_ACCOUNT)

			if (isIsolated) {
				dispatch(
					setOpenModalWithParams({
						type: 'futures_deposit_withdraw_isolated_margin',
						params: {
							tab: ManageModalType.Deposit,
						},
					})
				)
			} else {
				dispatch(
					setOpenModalWithParams({
						type: 'manage_wallet',
						params: { tab: DrawerTab.WITHDRAW },
					})
				)
			}
		}
	}, [dispatch, isDisabled, isIsolated, trackDepositFlow])

	return (
		<div>
			{isIsolated ? (
				<InputHeaderRow
					disabled={isDisabled}
					label={
						<InputTitle>
							<span>Size:</span>
							{wei(maxUsdInputAmount).gt(availableOiUsd) ? (
								<InputTitleSpan invalid={availableOiUsd.lt(susdSize || 0)}>
									Available OI{' '}
									{assetInputType === 'usd'
										? formatDollars(availableOiUsd, { suggestDecimals: true })
										: formatCryptoCurrency(availableOiNative, { suggestDecimals: true })}
								</InputTitleSpan>
							) : (
								<InputTitleSpan invalid={wei(maxUsdInputAmount).lt(susdSize || 0)}>
									{assetInputType === 'usd'
										? formatDollars(maxUsdInputAmount, { suggestDecimals: true })
										: formatCryptoCurrency(maxNativeValue, { suggestDecimals: true })}
								</InputTitleSpan>
							)}
							<ManageAccountIcon disabled={isDisabled} onClick={handleOnManageClick} />
						</InputTitle>
					}
					rightElement={
						<InputHelpers>
							<CalculatorContainer onClick={handleShowProfitCalculator}>
								Calculator
								<StyledCalculatorIcon />
							</CalculatorContainer>
						</InputHelpers>
					}
				/>
			) : (
				<TradePanelAvailableMargin />
			)}

			<NumericInput
				invalid={invalid}
				dataTestId={`set-order-size-amount-susd${isMobile ? '-mobile' : '-desktop'}`}
				disabled={isDisabled}
				right={<DenominationToggle />}
				value={assetInputType === 'usd' ? susdSize : nativeSize}
				placeholder="0.00"
				onChange={onChangeValue}
			/>
		</div>
	)
})

const InputHelpers = styled.div`
	display: flex;
`
const CalculatorContainer = styled(FlexDiv)`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
	gap: 5px;
	cursor: pointer;
	justify-content: flex-end;
	align-items: center;
`

const InputTitleSpan = styled(BaseInputTitleSpan)`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
`

const InputTitle = styled(BaseInputTitle)`
	display: flex;
	align-items: center;
	gap: 5px;
`

const ManageAccountIcon = styled(DepositWithdrawArrowsIcon)<{ disabled: boolean }>`
	cursor: pointer;
	opacity: ${(props) => (props.disabled ? 0.7 : 1)};
	path {
		fill: ${(props) => props.theme.colors.selectedTheme.newTheme.text.preview};
	}
`

const StyledCalculatorIcon = styled(CalculatorIcon)`
	path {
		fill: ${(props) => props.theme.colors.selectedTheme.newTheme.text.preview};
	}
`

export default OrderSizing
